import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarBlank, FirstAidKit, Money, HourglassMedium, Infinity as InfinityIcon, SignOut, IdentificationBadge, Cake, ChartBar, MagnifyingGlass } from "@phosphor-icons/react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-card border border-border rounded-md p-6 hover:-translate-y-1 hover:shadow-md transition-transform rise-in">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">{label}</p>
        <Icon size={20} weight="duotone" className="text-indigo-600" />
      </div>
      <p className="font-heading text-3xl font-semibold tracking-tight mt-3">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

const RANGE_LABELS = { ytd: "Fiscal YTD (Jun 1)", "30d": "Last 30 days", "90d": "Last 90 days", all: "All time" };

const nextSickReset = () => {
  const now = new Date();
  const year = (now.getMonth() > 5 || (now.getMonth() === 5 && now.getDate() >= 1)) ? now.getFullYear() + 1 : now.getFullYear();
  return `Jun 1, ${year}`;
};

function TeamAnalytics() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("ytd");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/analytics/team", { params: { range } }).then((r) => setData(r.data)).catch(() => {});
  }, [range]);

  const rows = (data?.rows || []).filter((r) =>
    !search || r.full_name.toLowerCase().includes(search.toLowerCase()) || (r.department || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="bg-card border border-border rounded-md" data-testid="team-analytics-widget">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <h3 className="font-heading font-semibold tracking-tight flex items-center gap-2">
          <ChartBar size={19} weight="duotone" className="text-indigo-600" /> Team time-off analytics
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="analytics-search-input" placeholder="Search member or dept…" className="h-9 w-52 pl-8"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger data-testid="analytics-range-select" className="h-9 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(RANGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="analytics-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-6 py-3 font-semibold">Member</th>
              <th className="px-3 py-3 font-semibold text-right">PTO days</th>
              <th className="px-3 py-3 font-semibold text-right">Sick days</th>
              <th className="px-3 py-3 font-semibold text-right">Unpaid days</th>
              <th className="px-3 py-3 font-semibold text-right">Total off</th>
              <th className="px-3 py-3 font-semibold text-right">Upcoming</th>
              <th className="px-3 py-3 font-semibold text-right">Pending</th>
              <th className="px-6 py-3 font-semibold text-right">Sick bal.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`analytics-row-${r.user_id}`} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-3">
                  <Link to={`/team/${r.user_id}`} className="font-medium hover:text-indigo-600 transition-colors">{r.full_name}</Link>
                  {r.department && <span className="text-xs text-muted-foreground ml-2">{r.department}</span>}
                </td>
                <td className="px-3 py-3 text-right" title={`${r.pto_hours}h`}>{r.pto_days}</td>
                <td className="px-3 py-3 text-right" title={`${r.sick_hours}h`}>{r.sick_days}</td>
                <td className="px-3 py-3 text-right" title={`${r.unpaid_hours}h`}>{r.unpaid_days}</td>
                <td className="px-3 py-3 text-right font-semibold" title={`${r.total_hours}h`}>{r.total_days}</td>
                <td className="px-3 py-3 text-right text-muted-foreground">{r.upcoming_days}</td>
                <td className="px-3 py-3 text-right">{r.pending_requests > 0 ? <Badge className="bg-amber-100 text-amber-800">{r.pending_requests}</Badge> : "—"}</td>
                <td className="px-6 py-3 text-right text-muted-foreground">{r.sick_balance_hours}h</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-6 py-8 text-muted-foreground">No members match your search.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && <p className="px-6 py-3 text-xs text-muted-foreground border-t border-border">Days used {fmtDate(data.start)} → {fmtDate(data.end)} · 1 day = 8h · Upcoming = approved days off ahead. Hover a number for exact hours.</p>}
    </div>
  );
}

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-red-100 text-red-700",
  rejected: "bg-red-100 text-red-700",
};

export default function DashboardPage() {
  const { user, hasPerm } = useAuth();
  const [paycheck, setPaycheck] = useState(null);
  const [requests, setRequests] = useState([]);
  const [pendingNotes, setPendingNotes] = useState([]);
  const [attention, setAttention] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get(`/payroll/next-paycheck/${user.id}`).then((r) => setPaycheck(r.data)).catch(() => {});
    api.get("/timeoff").then((r) => setRequests(r.data)).catch(() => {});
    if (hasPerm("sicknotes.review")) {
      api.get("/sick-notes", { params: { status: "pending" } }).then((r) => setPendingNotes(r.data)).catch(() => {});
    }
    if (hasPerm("licenses.review")) {
      api.get("/licenses/attention").then((r) => setAttention(r.data)).catch(() => {});
    }
    if (hasPerm("users.manage")) {
      api.get("/milestones", { params: { status: "pending" } }).then((r) => setMilestones(r.data)).catch(() => {});
      api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    }
  }, [user.id]);

  const forceLogoutAll = async () => {
    try {
      const { data } = await api.post("/auth/force-logout-all");
      toast.success(`All users force logged out — ${data.revoked} session(s) revoked. Your session stays active.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Force logout failed");
    }
  };

  const isApprover = hasPerm("timeoff.approve");
  const pending = requests.filter((r) => r.status === "pending");
  const mine = requests.filter((r) => r.user_id === user.id).slice(0, 5);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="dashboard-greeting">
            Hello, {user.full_name.split(" ")[0]}
          </h1>
        </div>
        {hasPerm("sessions.revoke") && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="force-logout-all-btn" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
                <SignOut size={15} className="mr-1.5" /> Force logout all users
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="force-logout-all-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Force logout all users?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every active session except yours will be revoked immediately. Everyone will need to sign in again with a fresh email code. This action is recorded in the audit trail.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="force-logout-all-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="force-logout-all-confirm" onClick={forceLogoutAll} className="bg-red-600 hover:bg-red-700 text-white">
                  Force logout everyone
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-pto" icon={InfinityIcon} label="PTO Policy" value="Unlimited" sub="book 30+ days ahead · short-notice unpaid" />
        <StatCard testId="stat-sick" icon={FirstAidKit} label="Sick Balance" value={`${user.sick_balance_hours}h`} sub={`resets ${nextSickReset()} · doctor's note required for paid sick leave`} />
        <StatCard testId="stat-paycheck" icon={Money} label="Next Paycheck (gross)" value={paycheck ? fmtMoney(paycheck.gross_estimate) : "—"} sub={paycheck ? `Pay date ${fmtDate(paycheck.pay_date)}` : ""} />
        <StatCard testId="stat-pending" icon={HourglassMedium} label={isApprover ? "Pending Approvals" : "My Pending Requests"} value={isApprover ? pending.length : mine.filter((r) => r.status === "pending").length} sub={isApprover ? `${pendingNotes.length} sick notes to review` : "awaiting review"} />
      </div>

      {user.role === "super_admin" && <TeamAnalytics />}

      {attention && (attention.missing.length > 0 || attention.expiring.length > 0 || attention.pending_review > 0) && (
        <div className="bg-card border border-border rounded-md" data-testid="license-attention-widget">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="font-heading font-semibold tracking-tight flex items-center gap-2">
              <IdentificationBadge size={19} weight="duotone" className="text-indigo-600" /> Licensing needs attention
            </h3>
            <Link to="/licenses" data-testid="attention-licenses-link" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">Open licenses</Link>
          </div>
          <div className="px-6 py-4 space-y-2 text-sm">
            {attention.pending_review > 0 && (
              <div><Badge className="bg-amber-100 text-amber-800 mr-2">{attention.pending_review} pending review</Badge>uploads awaiting your approval</div>
            )}
            {attention.missing.map((m, i) => (
              <div key={`m${i}`} data-testid={`attention-missing-${m.state}-${m.user_id}`}>
                <Badge className="bg-red-100 text-red-700 mr-2">missing</Badge>
                <strong>{m.user_name}</strong> — {m.state}: {m.reason}
              </div>
            ))}
            {attention.expiring.map((e, i) => (
              <div key={`e${i}`} data-testid={`attention-expiring-${e.state}-${e.user_id}`}>
                <Badge className={`mr-2 ${e.days_left < 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                  {e.days_left < 0 ? "EXPIRED" : `${e.days_left}d left`}
                </Badge>
                <strong>{e.user_name}</strong> — {e.state} license expires {fmtDate(e.expiration_date)}
              </div>
            ))}
          </div>
        </div>
      )}

      {hasPerm("users.manage") && (milestones.length > 0 || reviews.some((r) => r.status !== "approved")) && (
        <div className="bg-card border border-border rounded-md" data-testid="people-ops-widget">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="font-heading font-semibold tracking-tight flex items-center gap-2">
              <Cake size={19} weight="duotone" className="text-indigo-600" /> Milestones & reviews
            </h3>
            <Link to="/reviews" data-testid="people-ops-link" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">Open reviews</Link>
          </div>
          <div className="px-6 py-4 space-y-2 text-sm">
            {milestones.slice(0, 4).map((m) => (
              <div key={m.id} data-testid={`widget-milestone-${m.id}`}>
                <Badge className="bg-indigo-100 text-indigo-800 mr-2">{m.years} years</Badge>
                <strong>{m.user_name}</strong> — anniversary {fmtDate(m.milestone_date)} (reminding daily until acknowledged)
              </div>
            ))}
            {reviews.filter((r) => r.status === "pending").length > 0 && (
              <div><Badge className="bg-amber-100 text-amber-800 mr-2">{reviews.filter((r) => r.status === "pending").length} pending</Badge>annual performance reviews to complete</div>
            )}
            {reviews.filter((r) => r.status === "submitted").length > 0 && (
              <div><Badge className="bg-sky-100 text-sky-800 mr-2">{reviews.filter((r) => r.status === "submitted").length} submitted</Badge>awaiting super admin approval</div>
            )}
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="font-heading font-semibold tracking-tight">{isApprover ? "Pending time off" : "My recent requests"}</h3>
            <Link to="/timeoff" data-testid="dashboard-timeoff-link" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">View all</Link>
          </div>
          <div className="divide-y divide-border">
            {(isApprover ? pending : mine).slice(0, 5).map((r) => (
              <div key={r.id} className="px-6 py-3.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{isApprover ? r.user_name : r.type.toUpperCase()} {isApprover && <span className="text-muted-foreground">· {r.type.toUpperCase()}</span>}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(r.start_date)} → {fmtDate(r.end_date)} · {r.total_hours}h</p>
                </div>
                <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
              </div>
            ))}
            {(isApprover ? pending : mine).length === 0 && (
              <p className="px-6 py-8 text-sm text-muted-foreground">Nothing here yet.</p>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-md">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-heading font-semibold tracking-tight">Next paycheck breakdown</h3>
          </div>
          {paycheck ? (
            <div className="px-6 py-4 space-y-2.5 text-sm" data-testid="paycheck-breakdown">
              <div className="flex justify-between"><span className="text-muted-foreground">Period</span><span>{fmtDate(paycheck.period_start)} → {fmtDate(paycheck.period_end)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Base pay ({paycheck.pay_type})</span><span>{fmtMoney(paycheck.base_pay)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">PTO hours paid</span><span>{paycheck.pto_hours_paid}h</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sick hours paid</span><span>{paycheck.sick_hours_paid}h</span></div>
              <div className="flex justify-between text-red-600"><span>Unpaid deduction ({paycheck.sick_hours_unpaid + paycheck.other_unpaid_hours}h)</span><span>−{fmtMoney(paycheck.unpaid_deduction)}</span></div>
              <div className="flex justify-between border-t border-border pt-2.5 font-semibold"><span>Gross estimate</span><span className="text-indigo-700">{fmtMoney(paycheck.gross_estimate)}</span></div>
              <p className="text-xs text-muted-foreground pt-1" data-testid="commissions-note">If you are owed commissions, they are <strong>not reflected</strong> in this estimate.</p>
            </div>
          ) : <p className="px-6 py-8 text-sm text-muted-foreground">Calculating…</p>}
        </div>
      </div>
    </div>
  );
}
